<template>
    <div class="col-12 debts-page">
        <div class="card card-outline card-secondary debts-shell">
            <!-- Encabezado: misma línea visual que Pagos aprobados -->
            <div class="card-header debts-header">
                <div class="debts-header-content">
                    <div class="debts-heading">
                        <div class="debts-heading-icon" aria-hidden="true">
                            <i class="fa-solid fa-file-invoice-dollar"></i>
                        </div>
                        <div>
                            <h3 class="card-title debts-title">Lista de adeudos</h3>
                            <p class="debts-subtitle mb-0">
                                Consulta los adeudos por periodo, propiedad, arrendatario y empresa.
                            </p>
                        </div>
                    </div>
                    <div class="debts-header-actions">
                        <button type="button" class="btn btn-sm btn-outline-secondary"
                            :disabled="load" @click="list()">
                            <i class="fa-solid fa-rotate-right mr-1" :class="{ 'fa-spin': load }"></i>
                            Actualizar
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-primary"
                            :disabled="!hasActiveFilters" @click="resetFilters">
                            <i class="fa-solid fa-filter-circle-xmark mr-1"></i>
                            Limpiar filtros
                        </button>
                    </div>
                </div>
            </div>

            <div class="card-body debts-body">
                <!-- Filtros -->
                <section class="debts-filters" aria-label="Filtros de adeudos">
                    <div class="debts-section-heading">
                        <h4><i class="fa-solid fa-filter mr-2"></i>Filtros</h4>
                        <span class="debts-period-badge">
                            <i class="fa-regular fa-calendar mr-1"></i>
                            Consulta mensual
                        </span>
                    </div>
                    <div class="row debts-filters-grid">
                        <div class="col-12 col-md-6 col-xl-3">
                            <label for="debts-period">Periodo</label>
                            <date-picker
                                id="debts-period"
                                v-model="search.date"
                                type="month"
                                format="YYYY-MM"
                                valueType="format"
                                :clearable="false"
                                class="debts-date-picker"
                                placeholder="Selecciona un mes"
                            />
                        </div>
                        <div class="col-12 col-md-6 col-xl-3">
                            <label for="debts-building">Propiedad</label>
                            <select id="debts-building" v-model="search.building_id" class="form-control">
                                <option value="">Todas las propiedades</option>
                                <option v-for="building in catalogue.buildings" :key="building.id"
                                    :value="building.id">{{ building.name }}</option>
                            </select>
                        </div>
                        <div class="col-12 col-md-6 col-xl-3">
                            <label for="debts-user">Arrendatario</label>
                            <select id="debts-user" v-model="search.user_id" class="form-control">
                                <option value="">Todos los arrendatarios</option>
                                <option v-for="user in catalogue.users_profile" :key="user.id"
                                    :value="user.id">{{ user.name }} {{ user.last_name }}</option>
                            </select>
                        </div>
                        <div class="col-12 col-md-6 col-xl-3">
                            <label for="debts-company">Empresa</label>
                            <select id="debts-company" v-model="search.company_id" class="form-control">
                                <option value="">Todas las empresas</option>
                                <option v-for="company in catalogue.companies_all" :key="company.id"
                                    :value="company.id">{{ company.name }}</option>
                            </select>
                        </div>
                    </div>
                </section>

                <!-- Indicadores basados exclusivamente en los registros devueltos por la API -->
                <section class="debts-summary" aria-label="Resumen de adeudos">
                    <div class="debts-summary-card debts-summary-count">
                        <div class="debts-summary-icon"><i class="fa-solid fa-list-check"></i></div>
                        <div class="debts-summary-text">
                            <span>Registros encontrados</span>
                            <strong>{{ load ? '—' : summary.count }}</strong>
                        </div>
                    </div>
                    <div class="debts-summary-card debts-summary-total">
                        <div class="debts-summary-icon"><i class="fa-solid fa-wallet"></i></div>
                        <div class="debts-summary-text">
                            <span>Total de adeudos</span>
                            <strong v-if="load">—</strong>
                            <strong v-else>{{ summary.total | toCurrency }}</strong>
                        </div>
                    </div>
                    <div class="debts-summary-card debts-summary-rent">
                        <div class="debts-summary-icon"><i class="fa-solid fa-building"></i></div>
                        <div class="debts-summary-text">
                            <span>Rentas</span>
                            <strong v-if="load">—</strong>
                            <strong v-else>{{ summary.rent | toCurrency }}</strong>
                        </div>
                    </div>
                    <div class="debts-summary-card debts-summary-services">
                        <div class="debts-summary-icon"><i class="fa-solid fa-screwdriver-wrench"></i></div>
                        <div class="debts-summary-text">
                            <span>Servicios</span>
                            <strong v-if="load">—</strong>
                            <strong v-else>{{ summary.services | toCurrency }}</strong>
                        </div>
                    </div>
                </section>

                <!-- Resultados -->
                <section class="debts-results" aria-label="Resultados de adeudos" aria-live="polite">
                    <div class="debts-results-header">
                        <div>
                            <h4>Adeudos del periodo</h4>
                            <p>Desglose de rentas, servicios, impuestos, retenciones y conceptos.</p>
                        </div>
                        <span class="debts-result-count" v-if="!load && !hasError">
                            {{ contracts.length }} {{ contracts.length === 1 ? 'registro' : 'registros' }}
                        </span>
                    </div>

                    <div v-if="load" class="debts-state" role="status">
                        <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                        <h5>Cargando adeudos</h5>
                        <p>Consultando la información del periodo seleccionado.</p>
                    </div>
                    <div v-else-if="hasError" class="debts-state" role="alert">
                        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                        <h5>No fue posible consultar los adeudos</h5>
                        <p>Verifica la conexión e intenta nuevamente.</p>
                        <button type="button" class="btn btn-sm btn-outline-primary" @click="list()">
                            Reintentar
                        </button>
                    </div>
                    <div v-else-if="!contracts.length" class="debts-state">
                        <i class="fa-regular fa-folder-open" aria-hidden="true"></i>
                        <h5>Sin resultados</h5>
                        <p>No hay adeudos para los filtros seleccionados.</p>
                        <button v-if="hasActiveFilters" type="button"
                            class="btn btn-sm btn-outline-primary" @click="resetFilters">
                            Limpiar filtros
                        </button>
                    </div>
                    <template v-else>
                        <!-- Tabla de escritorio amplio: cinco columnas de conceptos, sin scroll vertical anidado -->
                        <div class="debts-desktop-table">
                            <table class="table table-bordered table-hover debts-table">
                                <thead>
                                    <tr>
                                        <th>Locales</th>
                                        <th>Arrendatario</th>
                                        <th>Folio</th>
                                        <th class="text-right">Renta</th>
                                        <th class="text-right">Servicios</th>
                                        <th class="text-right">Otros</th>
                                        <th>Impuestos y retenciones</th>
                                        <th class="text-right">Descuento</th>
                                        <th class="text-right">Total</th>
                                        <th>Fecha</th>
                                        <th v-for="n in 5" :key="'concept-header-' + n" class="debts-concept-heading">Concepto {{ n }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(contract, index) in contracts" :key="contract.id || index"
                                        :class="getRowClass(contract)">
                                        <td><strong class="debts-local">{{ contract.locales || 'S/N' }}</strong></td>
                                        <td class="debts-tenant">{{ contract.user || 'Sin arrendatario' }}</td>
                                        <td><span class="debts-folio">{{ contract.folio || '—' }}</span></td>
                                        <td class="text-right debts-number">{{ contract.renta | toCurrency }}</td>
                                        <td class="text-right debts-number">{{ contract.servicios | toCurrency }}</td>
                                        <td class="text-right debts-number">{{ contract.otro | toCurrency }}</td>
                                        <td>
                                            <div class="debts-tax-list">
                                                <span><small>IVA</small>{{ contract.iva | toCurrency }}</span>
                                                <span><small>IEPS</small>{{ contract.ieps | toCurrency }}</span>
                                                <span><small>ISR ret.</small>{{ contract.isr_ret | toCurrency }}</span>
                                                <span><small>IVA ret.</small>{{ contract.iva_ret | toCurrency }}</span>
                                            </div>
                                        </td>
                                        <td class="text-right debts-number">{{ contract.discount | toCurrency }}</td>
                                        <td class="text-right debts-total-cell">
                                            <strong>{{ contract.total | toCurrency }}</strong>
                                            <span v-if="contract.without_paying" class="debts-mini-badge debts-mini-badge-danger">
                                                Sin pagar
                                            </span>
                                            <span v-else-if="contract.pending" class="debts-mini-badge debts-mini-badge-warning">
                                                Pendiente
                                            </span>
                                        </td>
                                        <td class="debts-date">
                                            <span v-if="contract.fecha">{{ contract.fecha | toDateTime }}</span>
                                            <span v-else>—</span>
                                        </td>
                                        <td v-for="n in 5" :key="'concept-' + n" class="debts-concept-cell">
                                            <span v-if="getConcepts(contract)[n - 1] != null && getConcepts(contract)[n - 1] !== ''"
                                                class="debts-concept">{{ conceptLabel(getConcepts(contract)[n - 1]) }}</span>
                                            <span v-else class="debts-empty">—</span>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colspan="3"><strong>Totales del periodo mostrado</strong></td>
                                        <td class="text-right debts-number">{{ summary.rent | toCurrency }}</td>
                                        <td class="text-right debts-number">{{ summary.services | toCurrency }}</td>
                                        <td class="text-right debts-number">{{ summary.other | toCurrency }}</td>
                                        <td></td>
                                        <td class="text-right debts-number">{{ summary.discount | toCurrency }}</td>
                                        <td class="text-right debts-total-cell"><strong>{{ summary.total | toCurrency }}</strong></td>
                                        <td colspan="6"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <!-- Fichas para portátil, tableta y teléfono: misma información de la tabla -->
                        <div class="debts-cards">
                            <article v-for="(contract, index) in contracts" :key="contract.id || index"
                                class="debts-record" :class="getRowClass(contract)">
                                <header class="debts-record-header">
                                    <div class="debts-record-identity">
                                        <span class="debts-record-eyebrow">Local / Unidad</span>
                                        <h5>{{ contract.locales || 'S/N' }}</h5>
                                        <p><i class="fa-regular fa-user mr-1"></i>{{ contract.user || 'Sin arrendatario' }}</p>
                                        <div class="debts-record-meta">
                                            <span><i class="fa-solid fa-hashtag mr-1"></i>Folio: {{ contract.folio || '—' }}</span>
                                            <span><i class="fa-regular fa-calendar mr-1"></i>
                                                <template v-if="contract.fecha">{{ contract.fecha | toDateTime }}</template>
                                                <template v-else>Sin fecha</template>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="debts-record-amount">
                                        <span>Total adeudado</span>
                                        <strong>{{ contract.total | toCurrency }}</strong>
                                        <span v-if="contract.without_paying" class="debts-mini-badge debts-mini-badge-danger">
                                            <i class="fa-solid fa-circle-exclamation mr-1"></i>Sin pagar
                                        </span>
                                        <span v-else-if="contract.pending" class="debts-mini-badge debts-mini-badge-warning">
                                            <i class="fa-regular fa-clock mr-1"></i>Pendiente
                                        </span>
                                    </div>
                                </header>

                                <div class="debts-record-primary">
                                    <div><span>Renta</span><strong>{{ contract.renta | toCurrency }}</strong></div>
                                    <div><span>Servicios</span><strong>{{ contract.servicios | toCurrency }}</strong></div>
                                    <div><span>Otros</span><strong>{{ contract.otro | toCurrency }}</strong></div>
                                    <div><span>Descuento</span><strong>{{ contract.discount | toCurrency }}</strong></div>
                                </div>

                                <div class="debts-record-bottom">
                                    <div class="debts-record-section">
                                        <h6><i class="fa-solid fa-scale-balanced mr-1"></i>Impuestos y retenciones</h6>
                                        <div class="debts-record-taxes">
                                            <div><span>IVA</span><strong>{{ contract.iva | toCurrency }}</strong></div>
                                            <div><span>IEPS</span><strong>{{ contract.ieps | toCurrency }}</strong></div>
                                            <div><span>ISR retenido</span><strong>{{ contract.isr_ret | toCurrency }}</strong></div>
                                            <div><span>IVA retenido</span><strong>{{ contract.iva_ret | toCurrency }}</strong></div>
                                        </div>
                                    </div>
                                    <div class="debts-record-section">
                                        <h6><i class="fa-solid fa-tags mr-1"></i>Conceptos</h6>
                                        <div class="debts-concept-columns">
                                            <div v-for="n in 5" :key="'card-concept-' + n" class="debts-concept-slot">
                                                <span class="debts-concept-title">Concepto {{ n }}</span>
                                                <strong v-if="getConcepts(contract)[n - 1] != null && getConcepts(contract)[n - 1] !== ''"
                                                    class="debts-concept">{{ conceptLabel(getConcepts(contract)[n - 1]) }}</strong>
                                                <span v-else class="debts-empty">—</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </div>

                        <footer class="debts-results-footer">
                            <span>Totales correspondientes a los {{ contracts.length }} registros obtenidos.</span>
                            <strong>Total: {{ summary.total | toCurrency }}</strong>
                        </footer>
                    </template>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import request from '@/services/request';
import Catalogue from '@/services/catalogue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
    name: 'debts-table',
    components: { DatePicker },
    data() {
        return {
            load: false,
            hasError: false,
            catalogue: new Catalogue(),
            contracts: [],
            requestSequence: 0,
            search: {
                date: '',
                building_id: '',
                user_id: '',
                company_id: '',
            },
        };
    },
    computed: {
        ...mapGetters({ authUser: 'auth/user' }),
        hasActiveFilters() {
            return this.search.building_id !== '' ||
                this.search.user_id !== '' ||
                this.search.company_id !== '' ||
                this.search.date !== this.moment().format('YYYY-MM');
        },
        summary() {
            return this.contracts.reduce((totals, contract) => {
                totals.count += 1;
                totals.total += this.toNumber(contract.total);
                totals.rent += this.toNumber(contract.renta);
                totals.services += this.toNumber(contract.servicios);
                totals.other += this.toNumber(contract.otro);
                totals.discount += this.toNumber(contract.discount);
                return totals;
            }, { count: 0, total: 0, rent: 0, services: 0, other: 0, discount: 0 });
        },
    },
    methods: {
        toNumber(value) {
            if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
            if (value == null || value === '') return 0;
            const parsed = Number(String(value).replace(/,/g, '').replace(/[^\d.-]/g, ''));
            return Number.isFinite(parsed) ? parsed : 0;
        },
        getConcepts(contract) {
            return Array.isArray(contract.item) ? contract.item : [];
        },
        conceptLabel(concept) {
            if (concept == null) return 'Sin descripción';
            if (typeof concept === 'object') {
                return concept.name || concept.concept || concept.description || 'Sin descripción';
            }
            return String(concept);
        },
        getRowClass(contract) {
            if (contract.without_paying) return 'debts-status-unpaid';
            if (contract.pending) return 'debts-status-pending';
            return '';
        },
        resetFilters() {
            this.search = {
                date: this.moment().format('YYYY-MM'),
                building_id: '',
                user_id: '',
                company_id: '',
            };
        },
        async list() {
            const sequence = ++this.requestSequence;
            this.load = true;
            this.hasError = false;
            try {
                // Se conserva el endpoint y el formato de los filtros originales.
                const { data } = await request.get('/contracts_all_debts', { ...this.search });
                if (sequence !== this.requestSequence) return;
                this.contracts = Array.isArray(data)
                    ? data
                    : (data && Array.isArray(data.data) ? data.data : []);
            } catch (error) {
                if (sequence !== this.requestSequence) return;
                console.error('Error al obtener adeudos:', error);
                this.hasError = true;
                this.contracts = [];
            } finally {
                if (sequence === this.requestSequence) this.load = false;
            }
        },
    },
    mounted() {
        // El watcher ejecuta la consulta inicial una sola vez.
        this.search.date = this.moment().format('YYYY-MM');
        this.catalogue.get_buildings();
        this.catalogue.get_companies_all();
        this.catalogue.get_users_profile(7);
    },
    watch: {
        search: {
            handler() { this.list(); },
            deep: true,
        },
    },
};
</script>

<style scoped>
.debts-page { padding-bottom: 20px; min-width: 0; }
.debts-shell { overflow: hidden; border-radius: 8px; }
.debts-header { padding: 17px 20px; background: #fff; }
.debts-header-content, .debts-heading, .debts-header-actions { display: flex; align-items: center; }
.debts-header-content { justify-content: space-between; gap: 16px; }
.debts-heading { gap: 12px; min-width: 0; }
.debts-heading-icon { display: flex; flex: 0 0 42px; align-items: center; justify-content: center; width: 42px; height: 42px; border-radius: 9px; background: #eef4fc; color: #315d94; font-size: 17px; }
.debts-title { float: none; margin: 0 0 5px !important; font-size: 18px; font-weight: 700; line-height: 1.25; color: #273648; }
.debts-subtitle { font-size: 12px; line-height: 1.5; color: #718096; }
.debts-header-actions { flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.debts-header-actions .btn { min-height: 35px; padding: 7px 11px; border-radius: 6px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.debts-body { min-width: 0; padding: 18px 20px 20px; }

/* Filtros */
.debts-filters { padding: 17px 18px 3px; margin-bottom: 20px; background: #f8f9fa; border: 1px solid #e3e6e9; border-radius: 8px; }
.debts-section-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 14px; }
.debts-section-heading h4 { margin: 0; font-size: 15px; font-weight: 700; color: #343a40; }
.debts-period-badge { display: inline-flex; align-items: center; padding: 5px 9px; border: 1px solid #dbe6f3; border-radius: 16px; background: #eff5fc; color: #4c698c; font-size: 11px; font-weight: 600; }
.debts-filters-grid { margin-right: -8px; margin-left: -8px; }
.debts-filters-grid > [class*="col-"] { padding: 0 8px; margin-bottom: 15px; min-width: 0; }
.debts-filters label { display: block; margin-bottom: 6px; font-size: 12px; font-weight: 600; color: #495057; }
.debts-filters .form-control, .debts-filters >>> .mx-input { width: 100%; height: 40px; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 6px; background: #fff; color: #344054; font-size: 13px; box-shadow: none; }
.debts-filters .form-control:focus, .debts-filters >>> .mx-input:focus { border-color: #80bdff; box-shadow: 0 0 0 3px rgba(0, 123, 255, .08); }
.debts-date-picker, .debts-filters >>> .mx-datepicker { width: 100%; }

/* Indicadores */
.debts-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 15px; margin-bottom: 20px; }
.debts-summary-card { display: flex; align-items: center; gap: 12px; min-width: 0; min-height: 88px; padding: 15px; border: 1px solid #e5e7eb; border-left: 4px solid #6c757d; border-radius: 8px; background: #fff; box-shadow: 0 2px 5px rgba(15, 23, 42, .035); }
.debts-summary-icon { display: flex; flex: 0 0 43px; align-items: center; justify-content: center; width: 43px; height: 43px; border-radius: 50%; background: #f1f3f5; color: #495057; font-size: 17px; }
.debts-summary-text { min-width: 0; }
.debts-summary-text span { display: block; margin-bottom: 4px; color: #697586; font-size: 11px; font-weight: 600; letter-spacing: .3px; text-transform: uppercase; }
.debts-summary-text strong { display: block; color: #202b3c; font-size: clamp(16px, 1.45vw, 22px); font-weight: 750; line-height: 1.2; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.debts-summary-total { border-left-color: #d89b22; }
.debts-summary-total .debts-summary-icon { background: #fff7e8; color: #9a680a; }
.debts-summary-rent { border-left-color: #4b72d9; }
.debts-summary-rent .debts-summary-icon { background: #eff4ff; color: #456ac8; }
.debts-summary-services { border-left-color: #28a745; }
.debts-summary-services .debts-summary-icon { background: #eaf8ef; color: #268844; }

/* Resultados y estados */
.debts-results { min-width: 0; overflow: hidden; border: 1px solid #dee2e6; border-radius: 8px; background: #fff; }
.debts-results-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 15px 17px; border-bottom: 1px solid #e9ecef; }
.debts-results-header h4 { margin: 0 0 3px; color: #2f343a; font-size: 15px; font-weight: 700; }
.debts-results-header p { margin: 0; color: #7c8794; font-size: 12px; }
.debts-result-count { display: inline-flex; flex: 0 0 auto; padding: 5px 9px; border-radius: 12px; background: #f1f3f5; color: #536070; font-size: 11px; font-weight: 700; }
.debts-state { padding: 58px 20px; text-align: center; }
.debts-state > i { display: block; margin-bottom: 13px; color: #aab3bf; font-size: 36px; }
.debts-state h5 { margin-bottom: 6px; color: #354052; font-size: 16px; font-weight: 700; }
.debts-state p { margin-bottom: 13px; color: #7a8794; font-size: 12px; }
.debts-state .btn { border-radius: 6px; }
.debts-results-footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 7px; padding: 12px 16px; border-top: 1px solid #e4e9ef; background: #f7f9fb; color: #64748b; font-size: 12px; }
.debts-results-footer strong { color: #233247; font-size: 14px; font-variant-numeric: tabular-nums; }

/* Tabla para pantallas muy amplias */
.debts-desktop-table { display: block; }
.debts-table { width: 100%; margin: 0; table-layout: fixed; border-collapse: separate; border-spacing: 0; }
.debts-table th, .debts-table td { overflow-wrap: anywhere; }
.debts-table thead th { padding: 11px 7px; border-bottom: 2px solid #d9dde1; background: #f1f3f5; color: #48515d; font-size: 10px; font-weight: 700; letter-spacing: .035em; text-transform: uppercase; vertical-align: middle; }
.debts-table tbody td { padding: 11px 7px; background: #fff; color: #465467; font-size: 11px; line-height: 1.5; vertical-align: middle; }
.debts-table tbody tr:hover td { background: #f8fafc; }
.debts-table tbody tr td:first-child { border-left: 3px solid #b7c1cf; }
.debts-table tbody tr.debts-status-pending td:first-child { border-left-color: #e7b344; }
.debts-table tbody tr.debts-status-unpaid td:first-child { border-left-color: #dc6b6b; }
/* Tabla compacta de 15 columnas: los conceptos mantienen columnas independientes. */
.debts-table thead th:nth-child(1) { width: 6%; }
.debts-table thead th:nth-child(2) { width: 12%; }
.debts-table thead th:nth-child(3) { width: 5%; }
.debts-table thead th:nth-child(4) { width: 7%; }
.debts-table thead th:nth-child(5) { width: 7%; }
.debts-table thead th:nth-child(6) { width: 6%; }
.debts-table thead th:nth-child(7) { width: 13%; }
.debts-table thead th:nth-child(8) { width: 7%; }
.debts-table thead th:nth-child(9) { width: 8%; }
.debts-table thead th:nth-child(10) { width: 8%; }
.debts-table thead th:nth-child(n+11) { width: 4.2%; }
.debts-local { color: #1d304a; }
.debts-tenant { font-weight: 600; }
.debts-folio { color: #64748b; font-variant-numeric: tabular-nums; }
.debts-number, .debts-total-cell { font-weight: 600; font-variant-numeric: tabular-nums; }
.debts-total-cell strong { display: block; color: #1f3d67; font-size: 12px; }
.debts-date { color: #687689 !important; }
.debts-tax-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 3px 8px; }
.debts-tax-list > span { display: flex; flex-direction: column; min-width: 0; font-size: 10px; font-variant-numeric: tabular-nums; }
.debts-tax-list small { color: #8793a2; font-size: 9px; }
.debts-concept-list { display: flex; flex-wrap: wrap; gap: 5px; }
.debts-concept { display: inline-block; max-width: 100%; padding: 4px 7px; border: 1px solid #dce8f7; border-radius: 5px; background: #f1f6fd; color: #3a628f; font-size: 10px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
.debts-empty { color: #99a3af; font-size: 11px; font-style: italic; }
.debts-mini-badge { display: inline-flex; align-items: center; align-self: flex-start; width: fit-content; margin-top: 5px; padding: 3px 7px; border: 1px solid transparent; border-radius: 12px; font-size: 10px; font-weight: 700; }
.debts-mini-badge-warning { border-color: #f6dfa9; background: #fff8e7; color: #926611; }
.debts-mini-badge-danger { border-color: #f0c2c2; background: #fff1f1; color: #b44646; }
.debts-table tfoot td { padding: 12px 7px; border-top: 2px solid #cbd5e1; background: #e9ecef; color: #344054; font-size: 11px; font-weight: 700; }

/* Fichas, en consonancia con el responsive de Pagos aprobados */
.debts-cards { display: none; }
.debts-record { min-width: 0; overflow: hidden; border: 1px solid #dde5ee; border-left: 4px solid #9aa9bb; border-radius: 10px; background: #fff; box-shadow: 0 2px 7px rgba(15, 23, 42, .04); }
.debts-record.debts-status-pending { border-left-color: #e7b344; }
.debts-record.debts-status-unpaid { border-left-color: #dc6b6b; }
.debts-record-header { display: flex; justify-content: space-between; gap: 15px; padding: 15px 16px; background: #f8fafc; border-bottom: 1px solid #e9eef4; }
.debts-record-identity { min-width: 0; }
.debts-record-eyebrow, .debts-record-amount > span:first-child { display: block; margin-bottom: 4px; color: #778499; font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.debts-record-identity h5 { margin: 0 0 5px; color: #263e5a; font-size: 16px; font-weight: 750; overflow-wrap: anywhere; }
.debts-record-identity p { margin: 0 0 9px; color: #45566e; font-size: 12px; font-weight: 600; overflow-wrap: anywhere; }
.debts-record-meta { display: flex; flex-wrap: wrap; gap: 5px 14px; color: #7a8796; font-size: 11px; }
.debts-record-amount { display: flex; flex: 0 0 auto; flex-direction: column; align-items: flex-end; justify-content: center; text-align: right; }
.debts-record-amount strong { color: #23486f; font-size: 19px; line-height: 1.2; font-weight: 750; font-variant-numeric: tabular-nums; }
.debts-record-primary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); border-bottom: 1px solid #e9eef4; }
.debts-record-primary > div { min-width: 0; padding: 13px 14px; border-right: 1px solid #edf1f5; }
.debts-record-primary > div:last-child { border-right: 0; }
.debts-record-primary span, .debts-record-taxes span { display: block; margin-bottom: 5px; color: #718096; font-size: 10px; font-weight: 650; text-transform: uppercase; letter-spacing: .03em; }
.debts-record-primary strong, .debts-record-taxes strong { display: block; color: #30445d; font-size: 13px; line-height: 1.35; font-weight: 700; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.debts-record-bottom { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
.debts-record-section { min-width: 0; padding: 14px 16px 16px; }
.debts-record-section + .debts-record-section { border-left: 1px solid #edf1f5; }
.debts-record-section h6 { margin: 0 0 11px; color: #46556b; font-size: 11px; font-weight: 750; }
.debts-record-taxes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 10px; }

@media (max-width: 1599px) {
    .debts-desktop-table { display: none; }
    .debts-cards { display: grid; grid-template-columns: 1fr; gap: 13px; padding: 13px; background: #f6f8fb; }
}
@media (min-width: 1200px) and (max-width: 1599px) {
    .debts-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .debts-record-header { flex-wrap: wrap; }
    .debts-record-amount { flex: 1 1 100%; align-items: flex-start; text-align: left; padding-top: 11px; border-top: 1px solid #e4eaf1; }
    .debts-record-primary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .debts-record-primary > div:nth-child(2) { border-right: 0; }
    .debts-record-primary > div:nth-child(-n+2) { border-bottom: 1px solid #edf1f5; }
}
@media (max-width: 1199px) {
    .debts-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
    .debts-header, .debts-body { padding: 14px; }
    .debts-header-content { align-items: flex-start; flex-direction: column; }
    .debts-header-actions { width: 100%; justify-content: flex-start; }
    .debts-filters { padding: 15px 15px 1px; }
    .debts-summary { gap: 10px; }
    .debts-summary-card { padding: 12px; min-height: 78px; }
    .debts-summary-icon { flex-basis: 36px; width: 36px; height: 36px; font-size: 15px; }
    .debts-record-bottom { grid-template-columns: 1fr; }
    .debts-record-section + .debts-record-section { border-left: 0; border-top: 1px solid #edf1f5; }
}
@media (max-width: 575px) {
    .debts-heading-icon { flex-basis: 36px; width: 36px; height: 36px; }
    .debts-title { font-size: 17px; }
    .debts-header-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .debts-header-actions .btn { min-width: 0; white-space: normal; }
    .debts-summary { grid-template-columns: 1fr; }
    .debts-summary-card { min-height: 68px; }
    .debts-summary-text strong { font-size: 19px; }
    .debts-results-header { align-items: flex-start; flex-direction: column; }
    .debts-cards { padding: 8px; gap: 10px; }
    .debts-record-header { flex-direction: column; gap: 12px; }
    .debts-record-amount { align-items: flex-start; text-align: left; padding-top: 11px; border-top: 1px solid #e4eaf1; }
    .debts-record-primary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .debts-record-primary > div { border-bottom: 1px solid #edf1f5; }
    .debts-record-primary > div:nth-child(even) { border-right: 0; }
    .debts-record-primary > div:nth-last-child(-n+2) { border-bottom: 0; }
    .debts-record-section { padding: 13px; }
}

/* Diseño neutro y conceptos completos: sin recortes ni colores por columna. */

.debts-desktop-table {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
}
.debts-table {
    min-width: 2100px;
    width: 100%;
    table-layout: fixed;
}
.debts-table thead th {
    background: #f1f3f5;
    color: #495057;
    border-bottom-color: #d9dde1;
}
.debts-table tbody td,
.debts-table tbody tr:nth-child(even) td {
    background: #fff;
    color: #495057;
}
/* .debts-table tbody tr:hover td { background: #f8f9fa !important; } */
.debts-table tbody tr td:first-child,
.debts-table tbody tr.debts-status-pending td:first-child,
.debts-table tbody tr.debts-status-unpaid td:first-child {
    border-left-color: #adb5bd;
}
/* Reparto real de ancho: 5 conceptos de 160px o más. */
.debts-table thead th:nth-child(1) { width: 95px; }
.debts-table thead th:nth-child(2) { width: 170px; }
.debts-table thead th:nth-child(3) { width: 85px; }
.debts-table thead th:nth-child(4),
.debts-table thead th:nth-child(5),
.debts-table thead th:nth-child(6) { width: 105px; }
.debts-table thead th:nth-child(7) { width: 180px; }
.debts-table thead th:nth-child(8),
.debts-table thead th:nth-child(9) { width: 105px; }
.debts-table thead th:nth-child(10) { width: 115px; }
.debts-table thead th:nth-child(n+11) { width: 160px; }
.debts-concept-heading { font-size: 11px !important; }
.debts-concept-cell { text-align: left; vertical-align: top !important; }
.debts-concept,
.debts-concept-cell .debts-concept,
.debts-concept-slot .debts-concept {
    display: block;
    width: 100%;
    max-width: none;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    color: #495057;
    font-size: 12px;
    line-height: 1.55;
    font-weight: 500;
    white-space: normal;
    overflow: visible;
    overflow-wrap: break-word;
    word-break: normal;
    text-overflow: clip;
}
.debts-concept-columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}
.debts-concept-slot {
    min-width: 0;
    padding: 12px;
    border: 1px solid #e2e5e9;
    border-radius: 7px;
    background: #fff;
}
.debts-concept-title { display: block; margin-bottom: 7px; color: #6c757d; font-size: 11px; font-weight: 700; }
.debts-record-primary > div:nth-child(n),
.debts-record-taxes > div { background: #fff; }
.debts-record-taxes > div { padding: 9px; border: 1px solid #eceff2; border-radius: 6px; }
.debts-record-amount strong, .debts-table tbody td:nth-child(9) strong { color: #343a40; }
.debts-mini-badge-warning, .debts-mini-badge-danger {
    border-color: #dee2e6;
    background: #f8f9fa;
    color: #495057;
}
.debts-record.debts-status-pending, .debts-record.debts-status-unpaid { border-left-color: #adb5bd; }
@media (max-width: 575px) {
    .debts-concept-columns { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
    .debts-page * { scroll-behavior: auto !important; transition: none !important; }
}
</style>
